// src/components/whiteboard/TeachingMaterialsLibrary.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { BookOpen, Calculator, Globe, Atom, Dna, History, Music, Search, Filter } from 'lucide-react';

const LibraryContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #f8fafc;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  background: white;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchBar = styled.div`
  flex: 1;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  width: 16px;
  height: 16px;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: #f9fafb;
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 200px;
  background: white;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
`;

const SubjectList = styled.div`
  padding: 8px;
`;

const SubjectItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  margin: 2px 0;
  border: none;
  background: ${props => props.active ? '#eff6ff' : 'transparent'};
  color: ${props => props.active ? '#1d4ed8' : '#374151'};
  text-align: left;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#eff6ff' : '#f9fafb'};
  }
`;

const SubjectIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MainContent = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const CategoryTab = styled.button`
  padding: 8px 16px;
  border: none;
  background: ${props => props.active ? '#3b82f6' : 'transparent'};
  color: ${props => props.active ? 'white' : '#6b7280'};
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#3b82f6' : '#f9fafb'};
    color: ${props => props.active ? 'white' : '#374151'};
  }
`;

const MaterialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const MaterialCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
    transform: translateY(-2px);
  }
`;

const MaterialIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  background: #eff6ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 24px;
`;

const MaterialTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  color: #374151;
  font-weight: 600;
`;

const MaterialDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.4;
`;

const TeachingMaterialsLibrary = ({ onAddToCanvas }) => {
  const [selectedSubject, setSelectedSubject] = useState('数学');
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [searchQuery, setSearchQuery] = useState('');

  const subjects = [
    { id: '数学', name: '数学', icon: <Calculator size={20} /> },
    { id: '物理', name: '物理', icon: <Atom size={20} /> },
    { id: '化学', name: '化学', icon: <Globe size={20} /> },
    { id: '生物', name: '生物', icon: <Dna size={20} /> },
    { id: '历史', name: '历史', icon: <History size={20} /> },
    { id: '音乐', name: '音乐', icon: <Music size={20} /> },
    { id: '语文', name: '语文', icon: <BookOpen size={20} /> },
    { id: '英语', name: '英语', icon: <BookOpen size={20} /> }
  ];

  const categories = ['全部', '格线', '计数器', '立体几何图形', '函数', '平面几何图形', '几何工具', '数字和符号', '七巧板', '扑克牌', '时间工具'];

  const materials = {
    '数学': [
      { id: 'quadratic', title: '二次函数', description: '抛物线图像和性质', icon: '📈', category: '函数' },
      { id: 'trigonometry', title: '三角函数', description: 'sin, cos, tan 函数图像', icon: '🌊', category: '函数' },
      { id: 'coordinate', title: '坐标系', description: '直角坐标系和极坐标系', icon: '📐', category: '格线' },
      { id: 'geometry', title: '几何图形', description: '三角形、圆形、多边形', icon: '🔺', category: '平面几何图形' },
      { id: 'calculator', title: '计算器', description: '科学计算器工具', icon: '🧮', category: '几何工具' },
      { id: 'tangram', title: '七巧板', description: '传统拼图游戏', icon: '🧩', category: '七巧板' }
    ],
    '物理': [
      { id: 'mechanics', title: '力学', description: '牛顿定律和运动学', icon: '⚡', category: '函数' },
      { id: 'electricity', title: '电学', description: '电路图和电磁场', icon: '🔌', category: '几何工具' },
      { id: 'optics', title: '光学', description: '光线和透镜', icon: '💡', category: '几何工具' },
      { id: 'thermodynamics', title: '热学', description: '温度和热量', icon: '🌡️', category: '计数器' }
    ],
    '化学': [
      { id: 'periodic', title: '元素周期表', description: '化学元素周期表', icon: '🧪', category: '几何工具' },
      { id: 'molecules', title: '分子结构', description: '3D分子模型', icon: '⚛️', category: '立体几何图形' },
      { id: 'reactions', title: '化学反应', description: '化学方程式', icon: '💥', category: '几何工具' }
    ],
    '生物': [
      { id: 'cell', title: '细胞结构', description: '动植物细胞模型', icon: '🔬', category: '立体几何图形' },
      { id: 'dna', title: 'DNA结构', description: '双螺旋结构模型', icon: '🧬', category: '立体几何图形' },
      { id: 'ecosystem', title: '生态系统', description: '食物链和生态平衡', icon: '🌿', category: '几何工具' }
    ]
  };

  const filteredMaterials = materials[selectedSubject]?.filter(material => {
    const matchesCategory = selectedCategory === '全部' || material.category === selectedCategory;
    const matchesSearch = material.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         material.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  }) || [];

  const handleAddMaterial = (material) => {
    if (onAddToCanvas) {
      onAddToCanvas(material);
    }
  };

  return (
    <LibraryContainer>
      <Header>
        <SearchBar>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="搜索教学素材..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBar>
        <FilterButton>
          <Filter size={16} />
          筛选
        </FilterButton>
      </Header>

      <Content>
        <Sidebar>
          <SubjectList>
            {subjects.map(subject => (
              <SubjectItem
                key={subject.id}
                active={selectedSubject === subject.id}
                onClick={() => setSelectedSubject(subject.id)}
              >
                <SubjectIcon>{subject.icon}</SubjectIcon>
                {subject.name}
              </SubjectItem>
            ))}
          </SubjectList>
        </Sidebar>

        <MainContent>
          <CategoryTabs>
            {categories.map(category => (
              <CategoryTab
                key={category}
                active={selectedCategory === category}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </CategoryTab>
            ))}
          </CategoryTabs>

          <MaterialsGrid>
            {filteredMaterials.map(material => (
              <MaterialCard
                key={material.id}
                onClick={() => handleAddMaterial(material)}
              >
                <MaterialIcon>{material.icon}</MaterialIcon>
                <MaterialTitle>{material.title}</MaterialTitle>
                <MaterialDescription>{material.description}</MaterialDescription>
              </MaterialCard>
            ))}
          </MaterialsGrid>
        </MainContent>
      </Content>
    </LibraryContainer>
  );
};

export default TeachingMaterialsLibrary;
